import {useMemo} from 'react';
import {useMergedFeatureFlagConfig} from '../Provider';
import {AvailableFeatureNames, Experiment, FeatureToggleConfig} from '../types';
import {ABTestGroup} from './types';
import {useAbTestExperimentsCookieByFeatureFlag} from './useAbTestExperimentsCookie';

export const useConfigWithExperimentData = (
  featureName: AvailableFeatureNames,
): FeatureToggleConfig => {
  const mergedFeatureFlagConfig = useMergedFeatureFlagConfig();
  const config = mergedFeatureFlagConfig[featureName];

  const abTestExperiments = useAbTestExperimentsCookieByFeatureFlag({
    featureName,
  });

  return useMemo(() => {
    if (!abTestExperiments.length) {
      return {...config, experiment: undefined} as FeatureToggleConfig;
    }

    for (const abTestExperiment of abTestExperiments) {
      if (
        isExperimentConfigured(
          'experiment' in config ? config.experiment : undefined,
          abTestExperiment,
        )
      ) {
        const mapping: Record<string, string> = {};
        mapping[abTestExperiment.group] = abTestExperiment.group;
        return {
          ...config,
          experiment: {
            name: abTestExperiment.key,
            mapping,
          },
          ...('pageTypes' in config
            ? {pageTypes: config.pageTypes}
            : {pageTypes: []}),
        };
      }
    }

    return {...config, experiment: undefined} as FeatureToggleConfig;
  }, [abTestExperiments, config]);
};

const isExperimentConfigured = (
  featureFlagExperiment: Experiment | Readonly<Experiment[]> | undefined,
  abTestExperiment: ABTestGroup,
) => {
  if (!featureFlagExperiment) {
    return false;
  }
  const experimentMatches = (exp: Experiment) =>
    exp.name === abTestExperiment.key &&
    exp.mapping &&
    Object.keys(exp.mapping).includes(abTestExperiment.group);
  return Array.isArray(featureFlagExperiment)
    ? featureFlagExperiment.find(experimentMatches)
    : experimentMatches(featureFlagExperiment as Experiment);
};
